import React from 'react';
import { isTrackReference, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import {
  Badge, BadgeText, Box, Center, HStack, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { PeerGoneBanner } from '@/common/components/oralExam/PeerGoneBanner';
import { OralExamQuestionText } from '@/common/components/OralExamQuestionText';
import { MeetingFillVideo, ParticipantTile } from '@/common/components/meeting/MeetingVideoTiles';
import { useCompleteIfAbsentOnPeerGoneClose } from '@/common/hooks/oralExam/useCompleteIfAbsentOnPeerGoneClose';
import { usePeerGoneClosingCountdown } from '@/common/hooks/oralExam/usePeerGoneClosingCountdown';
import {
  AuthoritativeCountdown,
  ONE_TO_ONE_COUNTDOWN_RING_SIZE,
} from '@/student/module/components/AuthoritativeCountdown';
import {
  LabStandInImage,
  useOralExamLabOverrides,
} from '@/common/oralExam/oralExamLabOverrides';

import {
  requestScreenShareFullscreen,
  selectStudentTrackReference,
} from './StudentLiveTile';

/** Shared 1:1 clock — same ring as the student 1:1 stage. */
export const LECTURER_ENGAGE_COUNTDOWN_RING_SIZE = ONE_TO_ONE_COUNTDOWN_RING_SIZE;

type LecturerConductingStageProps = {
  question?: string | null;
  questionId?: string | null;
  secondsPerQuestion?: number;
  questionMarkerStartedAt?: string | null;
  questionDeadline?: string | null;
  isPaused?: boolean;
  /** Drop-in Engage: show supervisor pause banner above the question card. */
  isDropIn?: boolean;
  /** 1-based index of the current question, for the "Question x of N" chip. */
  questionIndex?: number | null;
  totalQuestions?: number | null;
  /** When true, show the 1:1 student-left banner when the student identity is gone. */
  enablePeerGoneBanner?: boolean;
  /** Last asked question — enables the 30s closing countdown when peer is gone. */
  isLastQuestion?: boolean;
  /** POST complete-if-absent when the closing countdown reaches 0. */
  completeIfAbsent?: () => Promise<unknown>;
  /** Booked student — pin the stage to their camera, not the first remote (agent). */
  studentId?: string | null;
};

/**
 * In-progress 1:1 lecturer conducting stage: student camera fills the left,
 * question card on the right. Screen share (if any) and self-view are corner
 * PiPs on the student tile — share never steals the stage.
 */
export function LecturerConductingStage({
  question,
  questionId,
  secondsPerQuestion,
  questionMarkerStartedAt,
  questionDeadline,
  isPaused,
  isDropIn = false,
  questionIndex,
  totalQuestions,
  enablePeerGoneBanner = false,
  isLastQuestion = false,
  completeIfAbsent,
  studentId,
}: LecturerConductingStageProps) {
  const { t } = useTranslation();
  const lab = useOralExamLabOverrides();
  const screenPipRef = React.useRef<unknown>(null);
  const standIn = lab?.resolveTileMedia?.({
    bookingId: studentId ?? '',
    studentId: studentId ?? '',
  });
  const standInSupervisor = lab?.supervisorLiveImageUrl ?? null;

  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: true },
  ).filter(isTrackReference);
  const studentTrack = selectStudentTrackReference(cameraTracks, studentId)
    ?? (studentId
      ? null
      : cameraTracks.find((tr) => !tr.participant.isLocal) ?? null);
  const studentPresent = Boolean(studentTrack);
  const peerGone = enablePeerGoneBanner && !studentPresent;
  const { secondsRemaining, showClosingCountdown } = usePeerGoneClosingCountdown(
    peerGone,
    isLastQuestion,
  );
  const completeIfAbsentFn = completeIfAbsent ?? (async () => undefined);
  const {
    error: completeIfAbsentError,
    isCompleting: isCompletingIfAbsent,
    retry: retryCompleteIfAbsent,
  } = useCompleteIfAbsentOnPeerGoneClose({
    showClosingCountdown: showClosingCountdown && Boolean(completeIfAbsent),
    secondsRemaining,
    completeIfAbsent: completeIfAbsentFn,
    defaultErrorMessage: t('oralExam.peerGone.completeFailed', {
      defaultValue: 'Could not close the exam. Try again.',
    }),
  });
  const lecturerTrack = cameraTracks.find((tr) => tr.participant.isLocal) ?? null;

  const screenTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: true })
    .filter(isTrackReference);
  const screenTrack = selectStudentTrackReference(screenTracks, studentId)
    ?? (studentId
      ? null
      : screenTracks.find((tr) => !tr.participant.isLocal) ?? null);

  const studentName = standIn?.studentName
    || studentTrack?.participant.name
    || studentTrack?.participant.identity
    || '';
  const roomTitle = studentName
    ? t('oralExam.studentRoomTitle', { name: studentName })
    : t('oralExam.examRoomTitle');

  const showProgress = typeof questionIndex === 'number' && typeof totalQuestions === 'number'
    && totalQuestions > 0;

  const hasStudentVideo = Boolean(
    studentTrack
    && isTrackReference(studentTrack)
    && studentTrack.publication.track
    && !studentTrack.publication.isMuted,
  );

  const questionText = question?.trim();

  return (
    <VStack flex={1} minHeight={0} space="md" testID="lecturer-conducting-stage">
      {peerGone ? (
        <PeerGoneBanner
          variant="lecturer"
          showClosingCountdown={showClosingCountdown}
          secondsRemaining={secondsRemaining}
          errorMessage={completeIfAbsent ? completeIfAbsentError : null}
          onRetry={completeIfAbsent ? () => { retryCompleteIfAbsent().catch(() => undefined); } : undefined}
          isRetrying={isCompletingIfAbsent}
        />
      ) : null}
      <HStack justifyContent="space-between" alignItems="center">
        <Text size="lg" fontWeight="bold" color="$textHeading">
          {roomTitle}
        </Text>
        <HStack space="md" alignItems="center">
          {showProgress ? (
            <Box bg="$bgSubtle" px="$2.5" py="$1" borderRadius="$full" testID="lecturer-question-progress">
              <Text size="xs" color="$textBody" fontWeight="medium">
                {t('oralExam.questionProgress', { current: questionIndex, total: totalQuestions })}
              </Text>
            </Box>
          ) : null}
        </HStack>
      </HStack>

      <HStack flex={1} minHeight={0} space="lg" alignItems="stretch">
        <Box flex={1.2} minWidth={280} minHeight={0}>
          <Box
            flex={1}
            height="100%"
            position="relative"
            minHeight={200}
            testID="lecturer-student-video"
          >
            {standIn ? (
              <>
                <LabStandInImage
                  source={standIn.standInCameraUrl}
                  testID="patrol-lab-standin-conducting-student"
                />
                <Box position="absolute" top={6} left={6} zIndex={10}>
                  <Badge size="sm" variant="solid" action="error" borderRadius="$xs">
                    <BadgeText>
                      {t('oralExam.lecturer.cohort.liveBadge', { defaultValue: 'LIVE' })}
                    </BadgeText>
                  </Badge>
                </Box>
                {standInSupervisor != null ? (
                  <Box
                    position="absolute"
                    bottom={12}
                    right={12}
                    width={160}
                    height={120}
                    softShadow="3"
                    zIndex={10}
                    overflow="hidden"
                    borderRadius="$md"
                    testID="lecturer-self-pip"
                  >
                    <LabStandInImage
                      source={standInSupervisor}
                      testID="patrol-lab-standin-conducting-supervisor"
                    />
                  </Box>
                ) : null}
              </>
            ) : studentTrack ? (
              <>
                <ParticipantTile trackRef={studentTrack} rounded="lg" initialsSize="4xl" />
                {hasStudentVideo ? (
                  <Box position="absolute" top={6} left={6} zIndex={10}>
                    <Badge size="sm" variant="solid" action="error" borderRadius="$xs">
                      <BadgeText>
                        {t('oralExam.lecturer.cohort.liveBadge', { defaultValue: 'LIVE' })}
                      </BadgeText>
                    </Badge>
                  </Box>
                ) : null}
                {screenTrack && isTrackReference(screenTrack) ? (
                  <Pressable
                    ref={screenPipRef as React.Ref<never>}
                    position="absolute"
                    bottom={12}
                    right={12}
                    width={220}
                    height={140}
                    softShadow="3"
                    zIndex={11}
                    borderRadius="$md"
                    overflow="hidden"
                    bg="$bgDark"
                    borderWidth={1}
                    borderColor="$borderLight"
                    onPress={() => requestScreenShareFullscreen(screenPipRef.current, 'lecturer-screen-share-pip')}
                    accessibilityRole="button"
                    accessibilityLabel={t('oralExam.lecturer.cohort.zoomScreenShare', {
                      defaultValue: 'Zoom shared screen',
                    })}
                    testID="lecturer-screen-share-pip"
                  >
                    <MeetingFillVideo trackRef={screenTrack} contain />
                  </Pressable>
                ) : null}
                {lecturerTrack ? (
                  <Box
                    position="absolute"
                    bottom={12}
                    right={screenTrack ? 12 + 220 + 8 : 12}
                    width={160}
                    height={120}
                    softShadow="3"
                    zIndex={10}
                    testID="lecturer-self-pip"
                  >
                    <ParticipantTile trackRef={lecturerTrack} rounded="md" />
                  </Box>
                ) : null}
              </>
            ) : (
              <Center flex={1} minHeight={200} borderRadius="$lg" bg="$bgDark">
                <Text size="sm" color="$textHint">
                  {t('oralExam.awaitingStudentVideo')}
                </Text>
              </Center>
            )}
          </Box>
        </Box>

        <VStack flex={1} minWidth={280} space="md" minHeight={0}>
          {isDropIn && isPaused ? (
            <Box
              bg="$bgBrandSubtle"
              borderLeftWidth={4}
              borderLeftColor="$brandBase"
              borderRadius="$sm"
              px="$3"
              py="$2"
              testID="lecturer-engaged-pause-banner"
            >
              <Text size="sm" color="$textOnBrandSubtle">
                {t('oralExam.lecturer.engagedPauseBanner', {
                  defaultValue: 'Exam paused for this student. Nothing said now is part of the exam.',
                })}
              </Text>
            </Box>
          ) : null}
          <Box
            bg="$bgCard"
            borderRadius="$md"
            borderWidth={1}
            borderColor="$borderLight"
            p="$3"
            flex={1}
            minHeight={0}
            overflow="hidden"
            testID="lecturer-current-question-card"
          >
            <VStack flex={1} minHeight={0} space="sm">
              {/* Hug the question (min height = content). Cap at 50% so a long
                  question text still scrolls; leftover white space belongs to the clock. */}
              <Box
                flex={questionDeadline ? undefined : 1}
                flexGrow={questionDeadline ? 0 : 1}
                flexShrink={1}
                minHeight={0}
                maxHeight={questionDeadline ? '50%' : undefined}
                w="$full"
                minWidth={0}
                overflow="hidden"
                testID="lecturer-conducting-question-band"
              >
                <Box
                  flex={questionDeadline ? undefined : 1}
                  minHeight={0}
                  maxHeight={questionDeadline ? '100%' : undefined}
                  height={questionDeadline ? undefined : '100%'}
                  overflow="scroll"
                  sx={{
                    _web: {
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      maxHeight: '100%',
                      ...(questionDeadline ? {} : { height: '100%' }),
                      scrollbarGutter: 'auto',
                    },
                  }}
                  testID="lecturer-conducting-question-scroll"
                >
                  <VStack space="xs">
                    <Text size="xs" color="$textSubtle" fontWeight="semibold" textTransform="uppercase">
                      {t('oralExam.studentCurrentQuestionLabel')}
                    </Text>
                    {showProgress ? (
                      <Text size="sm" color="$textBody">
                        {t('oralExam.questionProgress', { current: questionIndex, total: totalQuestions })}
                      </Text>
                    ) : null}
                    {questionText ? (
                      <OralExamQuestionText
                        question={questionText}
                        size="xl"
                        titleColor="$textHeading"
                        textColor="$textHeading"
                        testID="lecturer-conducting-question-text"
                      />
                    ) : (
                      <Text size="xl" fontWeight="semibold" color="$textHeading">
                        {t('oralExam.noCurrentQuestion')}
                      </Text>
                    )}
                  </VStack>
                </Box>
              </Box>
              {questionDeadline ? (
                <Box
                  flex={1}
                  minHeight={LECTURER_ENGAGE_COUNTDOWN_RING_SIZE}
                  w="$full"
                  alignSelf="stretch"
                  justifyContent="center"
                  alignItems="center"
                  testID="lecturer-question-timer-slot"
                >
                  <VStack alignItems="center" space="xs" w="$full">
                    <AuthoritativeCountdown
                      key={`${questionId ?? question ?? 'none'}:${questionDeadline ?? 'none'}`}
                      ringSize={LECTURER_ENGAGE_COUNTDOWN_RING_SIZE}
                      secondsPerQuestion={secondsPerQuestion ?? 0}
                      questionMarkerStartedAt={questionMarkerStartedAt}
                      questionDeadline={questionDeadline}
                      isPaused={isPaused ?? false}
                    />
                    {isPaused ? (
                      <Text size="xs" color="$textSubtle" testID="lecturer-clock-paused">
                        {t('oralExam.clockPaused', { defaultValue: 'Clock paused' })}
                      </Text>
                    ) : null}
                  </VStack>
                </Box>
              ) : null}
            </VStack>
          </Box>
        </VStack>
      </HStack>
    </VStack>
  );
}
